import { useLazyGetPlaybookQuery } from '@carboncopyco/data';
import { CloseOutlineIcon, PlayIcon } from '@carboncopyco/icons';
import {
  type Playbook,
  TourStepEnginePreviewMessageType,
} from '@carboncopyco/types';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { MemoryRouter, Route, Routes } from 'react-router-dom';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import { useEditorPageInfo } from '../components/EditorContainer/hooks';
import GiveADemoModalExtended from '../components/GiveADemoModalExtended';
import InnerViewArea from '../components/InnerViewArea';
import PresentAndPreviewToolbar from '../components/PresentAndPreviewToolbar';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useDelayLoadingPlaybookTab } from '../hooks/useDelayLoadingPlaybookTab';
import useEditorGroupTab from '../hooks/useEditorGroupTab';
import { usePlaybookPresentations } from '../hooks/usePlaybookPresentations';
import rendererService from '../services/RendererService';
import { HOME_TAB_ID, useTabManager } from '../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../shared/utils';
import { useTabs, useUser } from '../store/context';
import { ResponsiveModeContextProvider } from '../store/context/ResponsiveModeContext';
import { previewFrameBorder } from './constants/previewFrameBorder';

const TourTabContentBody = ({ tab }: { tab: IGlobalTab }) => {
  const isPresentation = tab.type === TabType.GROUP_TOUR_PRESENTATION;
  const { getActiveGlobalTab, closeGroupTabs, closeGlobalTab } =
    useTabManager();
  const pageInfo = useEditorPageInfo(tab);
  const activeTab = getActiveGlobalTab();
  const isActiveTab = activeTab?.id === tab.id;

  const [isGiveADemoVisible, setIsGiveADemoVisible] = useState(false);
  const [getPlaybook] = useLazyGetPlaybookQuery();
  const { activeOrganization } = useUser();
  const [activePlaybook, setActivePlaybook] = useState<Playbook>();
  const { links } = useCustomNavigate();
  const { setActiveGlobalTabId, setRedirectRoute } = useTabs();
  const [isCreatePresentationLoading, setIsCreatePresentationLoading] =
    useState(true);
  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);
  const isArchived = tab.payload.play?.isDeleted || tab.payload.tour?.isDeleted;
  activePlaybook?.workspaceId;
  const { handlePlayPlaybook } = usePlaybookPresentations({
    organizationId: String(activeOrganization.id),
  } as { organizationId: string });
  const { updateEditorTabVisibility, getFirstGroupTabByGroupId } =
    useEditorGroupTab();

  const fetchPlaybook = async () => {
    const playbook = await getPlaybook({
      organizationId: String(activeOrganization.id),
      id: tab.payload.play.playbookId,
    }).unwrap();
    setActivePlaybook(playbook);
  };

  useEffect(() => {
    if (!tab?.payload?.play?.playbookId) return;
    fetchPlaybook();
  }, [tab?.payload?.play?.playbookId]);

  useEffect(() => {
    if (isInnerViewLoaded && isCreatePresentationLoading) {
      setIsCreatePresentationLoading(false);
    }
  }, [isInnerViewLoaded]);

  const onStartDemoClick = async () => {
    await window.dsDesktop.speakerPanel.closeFromSpeakerPanelIfOpen({
      resetData: true,
    });

    if (activePlaybook) {
      setIsGiveADemoVisible(true);
    } else {
      handlePlayPlaybook({
        playSubset: tab.payload.play,
      });
    }
  };

  const onExitPreviewClick = () => {
    if (tab.payload?.isOpenedFromEditor) {
      const editorTab = tab.group && getFirstGroupTabByGroupId(tab.group.id);
      if (editorTab) {
        updateEditorTabVisibility({
          editorTab,
          show: true,
        });
      }
      closeGlobalTab(tab.id);
      return;
    }

    closeGroupTabs(activeTab);
  };

  const ctaButton =
    tab.type === TabType.GROUP_TOUR_PREVIEW && !isArchived
      ? {
          title: 'Start demo',
          onClick: onStartDemoClick,
          icon: <PlayIcon />,
        }
      : {
          title: 'Exit preview',
          onClick: onExitPreviewClick,
          icon: <CloseOutlineIcon />,
        };

  const [canStartLoadingTab, setCanStartLoadingTab] = useState(isActiveTab);
  const [isTabCreated, setIsTabCreated] = useState(false);

  const {
    delayLoadingPlaybookTab,
    logPlaybookTabLoad,
    setTabLoadingOverriden,
    shouldLoadInBackground,
  } = useDelayLoadingPlaybookTab(tab);

  useEffect(() => {
    delayLoadingPlaybookTab(() => setCanStartLoadingTab(true));
  }, []);

  useEffect(() => {
    if (isActiveTab) {
      setCanStartLoadingTab(true);
      setTabLoadingOverriden();
    }
  }, [isActiveTab]);

  const loadUrl = async () => {
    const htmlPath = await window.dsDesktop.app.getHtmlPath('tour');

    const updatedTab = {
      ...tab,
      payload: {
        ...tab.payload,
        shouldLoadInBackground,
      },
      innerTabs: tab.innerTabs.map((innerTab) => {
        return {
          ...innerTab,
          url: `${htmlPath}`,
        };
      }),
    };

    await rendererService.createGlobalTab(updatedTab);

    setIsTabCreated(true);
  };

  useEffect(() => {
    if (!isTabCreated && canStartLoadingTab) {
      logPlaybookTabLoad();
      loadUrl();
    }
  }, [canStartLoadingTab, isTabCreated]);

  return (
    <>
      <div className="flex flex-col flex-1">
        <PresentAndPreviewToolbar
          tab={tab}
          onGoBack={() => {
            window.dsDesktop.ipcRenderer.send(
              AppEventsEnum.APP_TO_RELATED_VIEW,
              {
                innerTabId: String(tab.innerTabs[0].id),
                isReplicate: false,
                type: TourStepEnginePreviewMessageType.onBackStep,
              }
            );
          }}
          onGoForward={() => {
            window.dsDesktop.ipcRenderer.send(
              AppEventsEnum.APP_TO_RELATED_VIEW,
              {
                innerTabId: String(tab.innerTabs[0].id),
                isReplicate: false,
                type: TourStepEnginePreviewMessageType.onNextStep,
              }
            );
          }}
          onRestart={() => {
            window.dsDesktop.ipcRenderer.send(
              AppEventsEnum.APP_TO_RELATED_VIEW,
              {
                innerTabId: String(tab.innerTabs[0].id),
                isReplicate: false,
                type: TourStepEnginePreviewMessageType.onRestartTour,
              }
            );
          }}
          speakerPannel={{
            isVisible:
              isPresentation || tab.type === TabType.GROUP_TOUR_PREVIEW,
          }}
          ctaButton={isPresentation ? undefined : ctaButton}
          pageInfo={pageInfo}
        />
        <div
          className={classNames('flex flex-col grow shrink min-h-0', {
            [previewFrameBorder]: tab.type === TabType.GROUP_TOUR_PREVIEW,
          })}
        >
          <ResponsiveModeContextProvider>
            <InnerViewArea tab={tab} />
          </ResponsiveModeContextProvider>
        </div>
      </div>
      {isGiveADemoVisible && activePlaybook && (
        <GiveADemoModalExtended
          open
          workspaceId={tab.workspaceId || ''}
          entity={activePlaybook}
          onPresent={() => {
            setIsGiveADemoVisible(false);
            setIsCreatePresentationLoading(false);
          }}
          onSave={() => {
            const redirectUrl = links.getPlaybookPresentationTabLink(
              activePlaybook.id,
              tab.workspaceId
            );
            setActiveGlobalTabId(HOME_TAB_ID);
            setRedirectRoute(redirectUrl);
            setIsGiveADemoVisible(false);
            setIsCreatePresentationLoading(false);
          }}
          onClose={() => {
            setIsGiveADemoVisible(false);
            setIsCreatePresentationLoading(false);
          }}
        />
      )}
    </>
  );
};

const TourTabContent = ({ tab }: { tab: IGlobalTab }) => {
  return (
    <MemoryRouter>
      <Routes>
        <Route
          path="*"
          element={<TourTabContentBody tab={tab} />}
        />
      </Routes>
    </MemoryRouter>
  );
};

export default TourTabContent;
